import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { createSpeechEngine } from '../utils/speechEngine.js';
import { speakableText } from '../utils/speakable.js';

// Lecture à voix haute des réponses (synthèse vocale du navigateur, fr-FR).
// Une seule lecture à la fois dans toute l'application : l'identifiant du message
// lu est partagé par toutes les barres d'actions. Texte préparé par speakableText
// (pauses, tableaux, unités), lecture et échecs gérés par utils/speechEngine.js.

export { speakableText };

const engine = createSpeechEngine(
  typeof window !== 'undefined'
    ? { synth: window.speechSynthesis, Utterance: window.SpeechSynthesisUtterance }
    : {}
);

export const speechSupported = engine.supported;

export function stopSpeaking() {
  engine.stop();
}

// Chrome poursuit parfois la lecture après un rechargement de la page
if (engine.supported) window.addEventListener('pagehide', stopSpeaking);

/**
 * { supported, speaking, toggle } pour le message `id`.
 * @param {() => string} getText  Markdown de la réponse, lu au clic
 * @param {(message: string, tone: string) => void} [notify]  Échec ou avertissement (toast)
 */
export function useSpeech(id, getText, notify) {
  const current = useSyncExternalStore(engine.subscribe, engine.getSnapshot, () => null);
  const speaking = current === id;
  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  const toggle = useCallback(() => {
    if (engine.getSnapshot() === id) {
      engine.stop();
      return;
    }
    engine.speak(id, speakableText(getText()), {
      notify: (message, tone) => notifyRef.current?.(message, tone),
    });
  }, [id, getText]);

  // Message retiré de l'écran (régénéré, conversation changée) : la lecture s'arrête
  useEffect(() => () => {
    if (engine.getSnapshot() === id) engine.stop();
  }, [id]);

  return { supported: engine.supported, speaking, toggle };
}
